import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Activity as ActivityIcon,
  FileText,
  ShieldCheck,
  RefreshCw,
  Search,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return ts;
  }
}

const EVENT_TONE = {
  pdf_rewrite: "text-[#E2FF54]",
  login: "text-[#4ADE80]",
  signup_completed: "text-[#4ADE80]",
  signup_started: "text-neutral-400",
  logout: "text-neutral-500",
};

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?limit=500"),
        api.get("/admin/activities?limit=200"),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setActivities(a.data.activities || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadUserActivities = async (u) => {
    setSelectedUser(u);
    try {
      const { data } = await api.get(
        `/admin/activities?user_id=${u.id}&limit=200`
      );
      setActivities(data.activities || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearUserFilter = async () => {
    setSelectedUser(null);
    const { data } = await api.get("/admin/activities?limit=200");
    setActivities(data.activities || []);
  };

  const filteredUsers = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) ||
        u.name?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q)
    );
  }, [users, query]);

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-8 md:py-10 border-b border-neutral-900 flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="mono text-[11px] uppercase tracking-[0.32em] text-[#E2FF54]">
            // admin panel
          </p>
          <h1 className="display text-4xl md:text-5xl font-bold uppercase mt-2 leading-[0.95]">
            all clients &<br />
            <span className="text-neutral-500">their activities</span>
          </h1>
        </div>
        <button
          onClick={load}
          className="mono text-[11px] uppercase tracking-[0.24em] text-neutral-500 hover:text-white flex items-center gap-2 border border-neutral-800 hover:border-neutral-600 px-4 py-2 self-start"
          data-testid="admin-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 border-b border-neutral-900">
        <StatBlock
          icon={Users}
          label="total users"
          value={stats?.total_users ?? "—"}
        />
        <StatBlock
          icon={ShieldCheck}
          label="verified"
          value={stats?.verified_users ?? "—"}
        />
        <StatBlock
          icon={ActivityIcon}
          label="activities"
          value={stats?.total_activities ?? "—"}
        />
        <StatBlock
          icon={FileText}
          label="pdf rewrites"
          value={stats?.pdf_rewrites ?? "—"}
        />
        <StatBlock
          icon={Users}
          label="signups 24h"
          value={stats?.signups_last_24h ?? "—"}
          highlight
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-neutral-900">
        {/* Users */}
        <div className="lg:col-span-5 lg:border-r border-neutral-900 p-6 md:p-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ users ] {filteredUsers.length}
            </h2>
          </div>
          <div className="relative mb-4">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search email, name, phone"
              className="pl-9 rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-10 text-white"
              data-testid="admin-user-search"
            />
          </div>

          <div className="border border-neutral-900 max-h-[560px] overflow-y-auto" data-testid="admin-users-list">
            {filteredUsers.length === 0 ? (
              <div className="p-6 mono text-xs text-neutral-500 text-center">
                no users match
              </div>
            ) : (
              filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => loadUserActivities(u)}
                  data-testid={`admin-user-${u.email}`}
                  className={cn(
                    "w-full text-left border-b border-neutral-900 p-4 hover:bg-neutral-900/40 transition-colors flex items-center gap-3",
                    selectedUser?.id === u.id && "bg-[#E2FF54]/[0.05]"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="mono text-sm text-white truncate flex items-center gap-2">
                      {u.email}
                      {u.role === "admin" && (
                        <span className="text-[9px] text-[#E2FF54] border border-[#E2FF54]/40 px-1 uppercase">
                          admin
                        </span>
                      )}
                      {!u.verified && (
                        <span className="text-[9px] text-[#FF9994] border border-[#FF3B30]/40 px-1 uppercase">
                          unverified
                        </span>
                      )}
                    </div>
                    <div className="mono text-[11px] text-neutral-500 mt-0.5 flex items-center gap-3">
                      <span>{u.name || "—"}</span>
                      <span className="text-neutral-700">·</span>
                      <span>{u.phone || "—"}</span>
                    </div>
                    <div className="mono text-[10px] text-neutral-600 mt-1 uppercase tracking-[0.2em]">
                      {u.activity_count} events · joined {fmt(u.created_at)}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-neutral-700 shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Activities */}
        <div className="lg:col-span-7 p-6 md:p-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ activities ]{" "}
              {selectedUser && (
                <span className="text-[#E2FF54]">/ {selectedUser.email}</span>
              )}
            </h2>
            {selectedUser && (
              <button
                onClick={clearUserFilter}
                className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white"
                data-testid="admin-clear-filter"
              >
                clear filter
              </button>
            )}
          </div>

          <div className="border border-neutral-900 max-h-[600px] overflow-y-auto" data-testid="admin-activities-list">
            <div className="grid grid-cols-12 border-b border-neutral-800 bg-[#0a0a0a] sticky top-0 z-10">
              <div className="col-span-3 border-r border-neutral-900 px-3 py-3 mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                who
              </div>
              <div className="col-span-3 border-r border-neutral-900 px-3 py-3 mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                event
              </div>
              <div className="col-span-4 border-r border-neutral-900 px-3 py-3 mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                details
              </div>
              <div className="col-span-2 px-3 py-3 mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                when
              </div>
            </div>

            {activities.length === 0 ? (
              <div className="p-8 mono text-xs text-neutral-500 text-center">
                no activities recorded
              </div>
            ) : (
              activities.map((a, idx) => {
                const details = a.metadata || {};
                const detailText =
                  a.event === "pdf_rewrite"
                    ? `${details.filename || "file"} · ${details.pages ?? "?"}p`
                    : Object.entries(details)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(" · ") || "—";
                return (
                  <div
                    key={a.id}
                    className="row-anim grid grid-cols-12 border-b border-neutral-900 hover:bg-neutral-900/40"
                    style={{ animationDelay: `${idx * 12}ms` }}
                    data-testid={`admin-activity-${a.id}`}
                  >
                    <div className="col-span-3 border-r border-neutral-900 px-3 py-2.5 mono text-[11px] text-neutral-300 truncate">
                      {a.user?.email || a.user_id?.slice(0, 8) || "—"}
                    </div>
                    <div
                      className={cn(
                        "col-span-3 border-r border-neutral-900 px-3 py-2.5 mono text-[11px] uppercase tracking-[0.14em] truncate",
                        EVENT_TONE[a.event] || "text-neutral-400"
                      )}
                    >
                      {a.event}
                    </div>
                    <div className="col-span-4 border-r border-neutral-900 px-3 py-2.5 mono text-[11px] text-neutral-500 truncate">
                      {detailText}
                    </div>
                    <div className="col-span-2 px-3 py-2.5 mono text-[10px] text-neutral-500">
                      {fmt(a.timestamp)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function StatBlock({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className="border-r last:border-r-0 border-neutral-900 p-5 md:p-6">
      <div className="mono text-[9px] uppercase tracking-[0.24em] text-neutral-600 flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div
        className={cn(
          "mono text-3xl md:text-4xl mt-2",
          highlight ? "text-[#E2FF54]" : "text-white"
        )}
      >
        {value}
      </div>
    </div>
  );
}
